import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  FormControl,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  styled,
  Typography,
  Button,
  Stack,
  Divider,
  IconButton,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TableChartIcon from '@mui/icons-material/TableChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import MouseIcon from '@mui/icons-material/Mouse';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BusinessIcon from '@mui/icons-material/Business';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AutocompleteSelect from '../Widgets/AutocompleteSelect/AutocompleteSelect.jsx';
import CustomMonthYearIndividualDatePicker from '../Widgets/CustomDates/CustomMonthYearIndividualDatePicker/CustomMonthYearIndividualDatePicker';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  '& .MuiToggleButton-root': {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '8px !important',
    margin: '0 2px',
    padding: '6px 14px',
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.8rem',
    gap: 6,
    transition: 'all 0.2s ease',
    '&.Mui-selected': {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#fff',
      borderColor: 'transparent',
      boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
      '&:hover': {
        background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4296 100%)',
      },
    },
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

export default function FiltersBar({
  subAccountants = [],
  clients = [],
  activities = [],
  onFilterChange,
  direction,
  onViewChange,
  onDownloadClick,
  onEmployeeEfficiencyClick,
  clientFromCrm = null
}) {
  const { t } = useTranslation();

  const [selectedSubs, setSelectedSubs] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState('');

  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d;
  });
  const [endDate, setEndDate] = useState(new Date());

  const [viewType, setViewType] = useState('graph');

  const handleViewChange = (_, newView) => {
    if (newView) {
      setViewType(newView);
      onViewChange?.(newView);
    }
  };

  useEffect(() => {
    onFilterChange({
      subAccountantIds: selectedSubs,
      clientCaseIds: selectedClients,
      activityType: selectedActivity || null,
      startUtc: new Date(Date.UTC(
        startDate.getFullYear(),
        startDate.getMonth(),
        1
      )),
      endUtc: new Date(Date.UTC(
        endDate.getFullYear(),
        endDate.getMonth() + 1,
        0,
        23, 59, 59
      )),
    });
  }, [selectedSubs, selectedClients, selectedActivity, startDate, endDate, onFilterChange]);

  useEffect(() => {
    if (!clientFromCrm) return;
    setSelectedClients(prev => {
      const id = typeof clientFromCrm === 'string'
        ? clientFromCrm
        : clientFromCrm.caseId;
      if (prev.some(c => (c.caseId || c.value) === id)) return prev;
      const option = clients.find(c => (c.caseId || c.value) === id);
      if (!option) return prev;
      return [...prev, option];
    });
  }, [clientFromCrm, clients]);

  const handleStartDateChangeNew = useCallback(
    (newValue, type) => {
      const date = newValue instanceof Date ? newValue : new Date(newValue);
      const year = type === 'year' ? date.getFullYear() : startDate.getFullYear();
      const month = type === 'month' ? date.getMonth() : startDate.getMonth();
      setStartDate(new Date(Date.UTC(year, month, 1)));
    },
    [startDate]
  );

  const handleEndDateChangeNew = useCallback(
    (newValue, type) => {
      const date = newValue instanceof Date ? newValue : new Date(newValue);
      const year = type === 'year' ? date.getFullYear() : endDate.getFullYear();
      const month = type === 'month' ? date.getMonth() : endDate.getMonth();
      setEndDate(new Date(Date.UTC(year, month + 1, 0)));
    },
    [endDate]
  );

  return (
    <Paper
      elevation={0}
      sx={{
        mx: 1,
        mb: 2,
        p: 2,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        background: 'linear-gradient(135deg, #fafbff 0%, #ffffff 100%)',
      }}
    >
      {/* Row 1: View tabs + Actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <StyledToggleButtonGroup
          value={viewType}
          exclusive
          onChange={handleViewChange}
          aria-label="View Type"
          size="small"
        >
          <ToggleButton value="graph">
            <BarChartIcon fontSize="small" /> {t('SwitchToGraphView')}
          </ToggleButton>
          <ToggleButton value="table">
            <TableChartIcon fontSize="small" /> {t('SwitchToTableView')}
          </ToggleButton>
          <ToggleButton value="attendance">
            <AccessTimeIcon fontSize="small" /> {t('Attendance')}
          </ToggleButton>
          <ToggleButton value="mouse">
            <MouseIcon fontSize="small" /> {t('WorkInSystem')}
          </ToggleButton>
          <ToggleButton value="efficiency">
            <TrendingUpIcon fontSize="small" /> {t('EfficiencyReport')}
          </ToggleButton>
          <ToggleButton value="clientReport">
            <BusinessIcon fontSize="small" /> {t('ClientActivityReport')}
          </ToggleButton>
        </StyledToggleButtonGroup>

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={() => onDownloadClick(true)}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            {t('DownloadReport')}
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<TrendingUpIcon />}
            onClick={() => onEmployeeEfficiencyClick?.()}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4296 100%)',
              },
            }}
          >
            {t('EfficiencyReport')}
          </Button>
        </Stack>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Row 2: Filters */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
        {/* People filters */}
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
          <FilterListIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
          <AutocompleteSelect
            options={subAccountants}
            selectedValues={selectedSubs}
            onChange={(e, o) => setSelectedSubs(o)}
            label={t('SubAccountant')}
            placeholder={t('SelectSubAccountant')}
            isMultiple
            width="200px"
            inputHeight={40}
          />
          <AutocompleteSelect
            options={clients}
            selectedValues={selectedClients}
            onChange={(e, o) => setSelectedClients(o)}
            label={t('Client')}
            placeholder={t('SelectClient')}
            isMultiple
            width="200px"
            inputHeight={40}
          />
        </Stack>

        {/* Activity type */}
        <FormControl variant="standard" sx={{ minWidth: 180 }}>
          <Select
            value={selectedActivity}
            onChange={e => setSelectedActivity(e.target.value)}
            displayEmpty
            disableUnderline
            sx={{
              height: '40px',
              px: 1,
              borderRadius: 1,
              bgcolor: '#F5F5F6',
              border: '1px solid transparent',
              fontSize: '0.875rem',
              '&:focus-within': { borderColor: 'primary.main' },
              textAlign: direction !== 'rtl' ? 'right' : 'left',
              '& .MuiSelect-select': {
                textAlign: direction !== 'rtl' ? 'right' : 'left',
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: { direction },
                dir: direction,
              },
              MenuListProps: {
                sx: { textAlign: direction !== 'rtl' ? 'right' : 'left' },
              },
            }}
          >
            <MenuItem value="" dir={direction} sx={{ textAlign: direction === 'rtl' ? 'right' : 'left' }}>
              <em>{t('SelectActivityType')}</em>
            </MenuItem>
            {activities.map(opt => (
              <MenuItem
                key={opt.value}
                value={opt.value}
                dir={direction}
                sx={{ textAlign: direction === 'rtl' ? 'right' : 'left' }}
              >
                {t(opt.label)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Date range */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          <CalendarMonthIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
          <CustomMonthYearIndividualDatePicker
            year={dayjs(startDate)}
            month={dayjs(startDate)}
            onYearChange={y => handleStartDateChangeNew(y, 'year')}
            onMonthChange={m => {
              const d = m.toDate ? m.toDate() : new Date(m);
              handleStartDateChangeNew(d, 'month');
            }}
            labelYear={t('StartYear')}
            labelMonth={t('StartMonth')}
            height="40px"
            widthOuter="240px"
            widthInner="110px"
          />

          <Typography variant="body2" sx={{ color: 'text.secondary', px: 0.5 }}>–</Typography>

          <CustomMonthYearIndividualDatePicker
            year={dayjs(endDate)}
            month={dayjs(endDate)}
            onYearChange={y => handleEndDateChangeNew(y, 'year')}
            onMonthChange={m => {
              const d = m.toDate ? m.toDate() : new Date(m);
              handleEndDateChangeNew(d, 'month');
            }}
            labelYear={t('EndYear')}
            labelMonth={t('EndMonth')}
            height="40px"
            widthOuter="240px"
            widthInner="110px"
          />
        </Stack>
      </Box>
    </Paper>
  );
}

FiltersBar.propTypes = {
  subAccountants: PropTypes.array.isRequired,
  clients: PropTypes.array.isRequired,
  activities: PropTypes.array.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  direction: PropTypes.oneOf(['ltr', 'rtl']).isRequired,
  onViewChange: PropTypes.func,
  onDownloadClick: PropTypes.func.isRequired,
  onEmployeeEfficiencyClick: PropTypes.func,
};
